import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { showConfirm } from './ConfirmDialog';
import { showToast } from './Toast';
import { NEUTRAL, STATUS_COLORS, resolveTheme } from './designTokens';
import DarkHeader, { darkPrimary } from './components/primitives/DarkHeader';
import Button from './components/primitives/Button';
import ExerciseBuilder from './ExerciseBuilder';
import ExerciseBlockPreview from './ExerciseBlockPreview';
import { KLINIO_DEFAULT_ACCENT } from './BrandContext';

// ─── Video-embed helper ───────────────────────────────────────────────────────
function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
}

function VideoEmbed({ url }) {
  const embedUrl = getEmbedUrl(url);
  if (!embedUrl) return <p style={{ color: NEUTRAL.faint, fontSize: '0.82em', margin: '8px 0 0' }}>Ogiltig video-URL</p>;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, marginTop: '12px', borderRadius: '8px', overflow: 'hidden' }}>
      <iframe
        src={embedUrl}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Exercise video"
      />
    </div>
  );
}


// ─── Övningsrad (T-12): tid · namn · kategori, klick fäller ut video/beskrivning ──
function ExerciseRow({ exercise, theme, usage, expanded, onToggle, onEdit, onDuplicate, onDelete, last }) {
  const hasVideo = !!exercise.video_url;
  const hasImage = !!exercise.image_url;
  const usedIn = usage || [];
  return (
    <div style={{ borderBottom: last ? 'none' : `1px solid ${theme.card_border}`, background: expanded ? '#FAFBFD' : 'transparent' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', boxSizing: 'border-box' }}
      >
        <div style={{ width: '56px', height: '40px', borderRadius: '8px', flexShrink: 0, overflow: 'hidden', background: hasVideo ? NEUTRAL.ink : hasImage ? NEUTRAL.line : 'transparent', border: !hasVideo && !hasImage ? `1px dashed ${NEUTRAL.line}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
          {hasVideo
            ? <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M4 2.5v9l7-4.5z" fill="#FFFFFF" /></svg>
            : hasImage
              ? <img src={exercise.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={NEUTRAL.faint} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Ingen media"><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M21 16l-5-5-8 8" /></svg>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exercise.title}</div>
          <div style={{ fontSize: '12px', color: NEUTRAL.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[exercise.category || 'Ingen kategori', usedIn.length > 0 ? `används i ${usedIn.length} ${usedIn.length === 1 ? 'fas' : 'faser'}` : null].filter(Boolean).join(' · ')}
            {usedIn.length === 0 && <span style={{ color: NEUTRAL.faint }}> · inte i något program</span>}
          </div>
        </div>
        <span aria-hidden="true" style={{ fontSize: '12px', color: expanded ? NEUTRAL.faint : NEUTRAL.line }}>{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && (
        <div className="fr-exercise-detail" style={{ padding: '0 20px 14px 90px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          {(hasVideo || hasImage || exercise.blocks?.length > 0) && (
            <div style={{ width: '340px', maxWidth: '50%', flexShrink: 0 }}>
              {exercise.blocks?.length > 0
                ? <ExerciseBlockPreview blocks={exercise.blocks} theme={theme} />
                : hasVideo
                  ? <VideoEmbed url={exercise.video_url} />
                  : <img src={exercise.image_url} alt={exercise.title} style={{ width: '100%', borderRadius: '10px', display: 'block' }} />}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {exercise.description && <div style={{ fontSize: '13px', color: NEUTRAL.text, lineHeight: 1.5 }}>{exercise.description}</div>}
            <div style={{ fontSize: '12px', color: NEUTRAL.muted }}>
              {usedIn.length > 0 ? `Används i: ${usedIn.join(' · ')}` : 'Används inte i något program ännu'}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Button theme={theme} variant="primary" size="sm" onClick={onEdit}>Redigera</Button>
              <Button theme={theme} variant="secondary" size="sm" onClick={onDuplicate}>Duplicera</Button>
              <button type="button" onClick={onDelete} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: STATUS_COLORS.error, fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}>Ta bort</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Huvudkomponent: Exercises ───────────────────────────────────────────────
function Exercises({ therapist, accentColor = KLINIO_DEFAULT_ACCENT }) {
  const [exercises, setExercises] = useState([]);
  const [usage, setUsage] = useState({}); // exercise_id -> ["Program · Fas", ...]
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    setLoading(true);
    const [{ data }, { data: pe }] = await Promise.all([
      supabase.from('exercises').select('*').eq('therapist_id', therapist.id).order('created_at', { ascending: false }),
      supabase.from('program_exercises').select('exercise_id, block:program_blocks(title, program:programs(title, therapist_id))'),
    ]);
    setExercises(data || []);
    const map = {};
    (pe || []).forEach(row => {
      if (row.block?.program?.therapist_id && row.block.program.therapist_id !== therapist.id) return;
      const label = [row.block?.program?.title, row.block?.title].filter(Boolean).join(' · ');
      if (!map[row.exercise_id]) map[row.exercise_id] = [];
      if (label && !map[row.exercise_id].includes(label)) map[row.exercise_id].push(label);
    });
    setUsage(map);
    setLoading(false);
  };

  const deleteExercise = (id) => {
    showConfirm({
      title: 'Ta bort övning',
      description: 'Övningen tas inte bort från program där den redan används.',
      onConfirm: async () => {
        await supabase.from('exercises').delete().eq('id', id);
        setExpandedId(null);
        fetchExercises();
      },
    });
  };

  const duplicateExercise = async (ex) => {
    const { error } = await supabase.from('exercises').insert({
      therapist_id: therapist.id,
      title: `${ex.title} (kopia)`,
      description: ex.description || null,
      category: ex.category || null,
      video_url: ex.video_url || null,
      image_url: ex.image_url || null,
      blocks: ex.blocks || null,
    });
    if (error) { showToast('Kunde inte kopiera övningen.', 'error'); return; }
    showToast('Övning kopierad', 'success');
    fetchExercises();
  };

  const categories = [...new Set(exercises.map(e => e.category).filter(Boolean))].sort();

  const filtered = exercises.filter((e) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || e.title.toLowerCase().includes(term) || (e.description && e.description.toLowerCase().includes(term)) || (e.category && e.category.toLowerCase().includes(term));
    const matchesCategory = !filterCategory || e.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const theme = resolveTheme(accentColor);

  if (loading) return <p style={{ color: NEUTRAL.faint, fontSize: '0.9em' }}>Laddar övningar...</p>;

  if (selectedExercise !== null) {
    return (
      <ExerciseBuilder
        exercise={selectedExercise}
        therapist={therapist}
        accentColor={accentColor}
        onBack={() => setSelectedExercise(null)}
        onSaved={() => { setSelectedExercise(null); fetchExercises(); }}
      />
    );
  }

  const chipStyle = (active) => ({
    background: active ? '#FFFFFF' : 'rgba(255,255,255,0.08)',
    border: `1px solid ${active ? '#FFFFFF' : 'rgba(255,255,255,0.18)'}`,
    color: active ? NEUTRAL.ink : '#FFFFFF',
    padding: '6px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: active ? '600' : '400', cursor: 'pointer', fontFamily: 'inherit',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <DarkHeader
        theme={theme}
        title="Övningar"
        subtitle={`${exercises.length} ${exercises.length === 1 ? 'övning' : 'övningar'}${categories.length > 0 ? ` · ${categories.length} ${categories.length === 1 ? 'kategori' : 'kategorier'}` : ''}`}
        actions={<button type="button" onClick={() => setSelectedExercise({})} style={darkPrimary(theme)}>+ Ny övning</button>}
        search={{ value: searchTerm, onChange: setSearchTerm, placeholder: 'Sök övning…' }}
      >
        {categories.length > 0 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setFilterCategory('')} style={chipStyle(filterCategory === '')}>Alla · {exercises.length}</button>
            {categories.map(cat => (
              <button key={cat} type="button" onClick={() => setFilterCategory(filterCategory === cat ? '' : cat)} style={chipStyle(filterCategory === cat)}>
                {cat} · {exercises.filter(e => e.category === cat).length}
              </button>
            ))}
          </div>
        )}
      </DarkHeader>

      <div style={{ background: theme.card_bg, border: `1px solid ${theme.card_border}`, borderRadius: theme.card_radius, boxShadow: theme.card_shadow, overflow: 'hidden' }}>
        {exercises.length === 0 ? (
          <div style={{ margin: '12px', padding: '28px 20px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}` }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: NEUTRAL.ink, marginBottom: '4px' }}>Inga övningar ännu</div>
            <div style={{ fontSize: '12px', color: NEUTRAL.muted, marginBottom: '12px' }}>Skapa ditt övningsbibliotek – övningarna läggs sedan in i programmens faser.</div>
            <Button theme={theme} variant="primary" size="sm" onClick={() => setSelectedExercise({})}>+ Skapa din första övning</Button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ margin: '12px', padding: '24px', textAlign: 'center', borderRadius: '10px', border: `1px dashed ${theme.card_border}`, fontSize: '12px', color: NEUTRAL.muted }}>Inga träffar · prova ett annat sökord eller en annan kategori</div>
        ) : filtered.map((exercise, i) => (
          <ExerciseRow
            key={exercise.id}
            exercise={exercise}
            theme={theme}
            usage={usage[exercise.id]}
            expanded={expandedId === exercise.id}
            onToggle={() => setExpandedId(expandedId === exercise.id ? null : exercise.id)}
            onEdit={() => setSelectedExercise(exercise)}
            onDuplicate={() => duplicateExercise(exercise)}
            onDelete={() => deleteExercise(exercise.id)}
            last={i === filtered.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default Exercises;
